// ============================================================
// NAVIGATION CONFIG – Sidebar chuẩn cuối cùng
// 6 nhóm: Tổng quan | Nguồn lực | Thi công | Bảo dưỡng | Cá nhân | Quản trị
// Mỗi nhóm có context tag để lọc: foundation | resource | construction | maintenance | personal | admin
// ============================================================

export type UserRole =
  | 'super_admin'
  | 'admin'
  | 'director'
  | 'pm'
  | 'qa_qc'
  | 'qs'
  | 'site_manager'
  | 'warehouse'
  | 'accountant'
  | 'secretary'
  | 'supervisor'
  | 'staff'
  | 'site_foreman'
  | 'equipment_manager'
  | 'zone_manager'
  | 'agro_manager';

export interface NavItem {
  key: string;
  label: string;
  path: string;
  icon: string;
  badge?: string;
  roles: UserRole[];
  children?: NavItem[];
}

export interface NavGroup {
  key: string;
  label: string;
  items: NavItem[];
}


// ─── 6 nhóm menu chuẩn ──────────────────────────────────────────────────────

export const navigationConfig: NavGroup[] = [
  // ═══════════════════════════════════════════════════════════════════════════
  // A. TỔNG QUAN & NỀN TẢNG – Luôn hiển thị
  // ═══════════════════════════════════════════════════════════════════════════
  {
    key: 'foundation',
    label: 'Tổng quan',
    items: [
      {
        key: 'dashboard',
        label: 'Trang chủ',
        path: '/',
        icon: 'ri-dashboard-3-line',
        roles: ['super_admin', 'admin', 'director', 'pm', 'qa_qc', 'site_manager', 'warehouse', 'accountant', 'supervisor', 'staff', 'zone_manager', 'qs', 'secretary', 'agro_manager', 'equipment_manager', 'site_foreman'],
      },
      {
        key: 'projects',
        label: 'Dự án',
        path: '/projects',
        icon: 'ri-building-2-line',
        roles: ['super_admin', 'admin', 'director', 'pm', 'qa_qc', 'site_manager', 'warehouse', 'accountant', 'supervisor', 'zone_manager', 'qs', 'secretary', 'agro_manager', 'equipment_manager', 'site_foreman'],
      },
      {
        key: 'estimates',
        label: 'Dự toán',
        path: '/estimates',
        icon: 'ri-calculator-line',
        roles: ['super_admin', 'admin', 'director', 'pm', 'qs', 'site_manager'],
        children: [
          { key: 'estimates_list', label: 'Quản lý Dự toán', path: '/estimates', icon: 'ri-calculator-line', roles: ['super_admin', 'admin', 'director', 'pm', 'qs', 'site_manager'] },
          { key: 'norms_library', label: 'Thư viện Định mức', path: '/estimates/norms', icon: 'ri-book-read-line', roles: ['super_admin', 'admin', 'director', 'qs'] },
          { key: 'resource_rates', label: 'Bảng giá Nguồn lực', path: '/estimates/rates', icon: 'ri-money-dollar-circle-line', roles: ['super_admin', 'admin', 'director', 'qs', 'accountant'] }
        ]
      },
      {
        key: 'zones',
        label: 'Khu vực',
        path: '/zones',
        icon: 'ri-map-pin-line',
        roles: ['super_admin', 'admin', 'director', 'pm', 'site_manager', 'supervisor', 'zone_manager', 'agro_manager', 'site_foreman'],
      },
      {
        key: 'work-orders',
        label: 'Công việc',
        path: '/work-orders',
        icon: 'ri-task-line',
        roles: ['super_admin', 'admin', 'director', 'pm', 'qa_qc', 'site_manager', 'warehouse', 'accountant', 'supervisor', 'zone_manager', 'qs', 'secretary', 'agro_manager', 'equipment_manager', 'site_foreman'],
      },
      {
        key: 'requests',
        label: 'Đề xuất',
        path: '/requests',
        icon: 'ri-archive-line',
        roles: ['super_admin', 'admin', 'director', 'pm', 'site_manager', 'site_foreman', 'supervisor', 'staff', 'warehouse', 'accountant', 'secretary', 'zone_manager', 'agro_manager', 'equipment_manager'],
      },
      {
        key: 'approvals',
        label: 'Phê duyệt',
        path: '/approvals',
        icon: 'ri-checkbox-circle-line',
        roles: ['super_admin', 'admin', 'director', 'pm', 'qa_qc', 'site_manager', 'accountant', 'secretary', 'qs', 'supervisor'],
      },
      {
        key: 'notifications',
        label: 'Thông báo',
        path: '/notifications',
        icon: 'ri-notification-3-line',
        roles: ['super_admin', 'admin', 'director', 'pm', 'qa_qc', 'site_manager', 'warehouse', 'accountant', 'supervisor', 'staff', 'site_foreman', 'equipment_manager', 'zone_manager', 'qs', 'secretary', 'agro_manager'],
      },
      {
        key: 'reports',
        label: 'Báo cáo',
        path: '/reports',
        icon: 'ri-file-chart-line',
        roles: ['super_admin', 'admin', 'director', 'pm', 'accountant', 'supervisor', 'qa_qc', 'qs', 'site_manager', 'secretary'],
      },
      {
        key: 'project-documents',
        label: 'Hồ sơ công trình',
        path: '/documents',
        icon: 'ri-folder-open-line',
        roles: ['super_admin', 'admin', 'director', 'pm', 'qa_qc', 'site_manager', 'secretary', 'qs'],
      },
      {
        key: 'docs',
        label: 'Hướng dẫn sử dụng',
        path: '/docs',
        icon: 'ri-book-open-line',
        roles: ['super_admin', 'admin', 'director', 'pm', 'qa_qc', 'qs', 'site_manager', 'warehouse', 'accountant', 'secretary', 'supervisor', 'staff', 'site_foreman', 'equipment_manager', 'zone_manager', 'agro_manager'],
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // B. QUẢN TRỊ NGUỒN LỰC – Luôn hiển thị
  // ═══════════════════════════════════════════════════════════════════════════
  {
    key: 'resource',
    label: 'Nguồn lực',
    items: [
      {
        key: 'users',
        label: 'Tài khoản hệ thống',
        path: '/admin/users',
        icon: 'ri-user-settings-line',
        roles: ['super_admin'],
      },
      {
        key: 'company_users',
        label: 'Nhân sự',
        path: '/users',
        icon: 'ri-team-line',
        roles: ['super_admin', 'admin', 'director', 'pm', 'site_manager'],
      },

      {
        key: 'inventory',
        label: 'Kho vật tư',
        path: '/inventory',
        icon: 'ri-store-3-line',
        roles: ['super_admin', 'admin', 'director', 'pm', 'site_manager', 'warehouse', 'accountant', 'agro_manager', 'equipment_manager'],
      },

      {
        key: 'equipment',
        label: 'Thiết bị & Máy móc',
        path: '/equipment',
        icon: 'ri-tools-line',
        badge: '',
        roles: ['super_admin', 'admin', 'equipment_manager', 'supervisor', 'site_manager', 'director', 'pm'],
      },


      {
        key: 'utility',
        label: 'Điện – Nước & Tưới',
        path: '/utility',
        icon: 'ri-drop-line',
        badge: '',
        roles: ['super_admin', 'admin', 'equipment_manager', 'supervisor', 'site_manager', 'director', 'pm'],
      },
      {
        key: 'agro',
        label: 'Phân - thuốc',
        path: '/agro',
        icon: 'ri-flask-line',
        badge: '',
        roles: ['super_admin', 'admin', 'director', 'agro_manager', 'supervisor', 'site_manager', 'warehouse', 'pm'],
      },
    ],
  },


];

// ─── Role labels ──────────────────────────────────────────────────────────────

export const roleLabels: Record<UserRole, string> = {
  super_admin: 'Quản trị viên tối cao',
  admin: 'Quản trị viên',
  director: 'Giám đốc',
  pm: 'Quản lý dự án',
  qa_qc: 'QA/QC',
  qs: 'QS',
  site_manager: 'Chỉ huy trưởng',
  warehouse: 'Thủ kho',
  accountant: 'Kế toán',
  secretary: 'Thư ký dự án',
  supervisor: 'Giám sát',
  staff: 'Nhân viên',
  site_foreman: 'Tổ trưởng',
  equipment_manager: 'Quản lý thiết bị',
  zone_manager: 'Quản lý Khu vực',
  agro_manager: 'Quản lý Nông hóa phẩm',
};

// ─── Role badge colors ────────────────────────────────────────────────────────

export const roleBadgeColors: Record<UserRole, { bg: string; text: string; border: string }> = {
  super_admin: { bg: 'bg-slate-900', text: 'text-amber-400', border: 'border-amber-500/50' },
  admin: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-100' },
  director: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-100' },
  pm: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-100' },
  qa_qc: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-100' },
  qs: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-100' },
  site_manager: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-100' },
  warehouse: { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-100' },
  accountant: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-100' },
  secretary: { bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-100' },
  supervisor: { bg: 'bg-green-50', text: 'text-green-800', border: 'border-green-100' },
  staff: { bg: 'bg-lime-50', text: 'text-lime-700', border: 'border-lime-100' },
  site_foreman: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-100' },
  equipment_manager: { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-100' },
  zone_manager: { bg: 'bg-green-50', text: 'text-green-900', border: 'border-green-100' },
  agro_manager: { bg: 'bg-yellow-50', text: 'text-yellow-800', border: 'border-yellow-100' },
};

// ─── Role landing page map ────────────────────────────────────────────────────

export const roleLandingPageMap: Record<UserRole, string> = {
  super_admin: '/',
  admin: '/',
  director: '/',
  pm: '/pm-dashboard',
  qa_qc: '/qa-dashboard',
  qs: '/qs-dashboard',
  site_manager: '/site-manager',
  warehouse: '/warehouse-dashboard',
  accountant: '/accountant-dashboard',
  secretary: '/secretary-dashboard',
  supervisor: '/maintenance',
  staff: '/worker-dashboard',
  site_foreman: '/foreman',
  equipment_manager: '/equipment-dashboard',
  zone_manager: '/zone-ops',
  agro_manager: '/agro-dashboard',
};

// ─── Quick actions theo role ──────────────────────────────────────────────────

export interface RoleQuickAction {
  key: string;
  label: string;
  icon: string;
  path: string;
}

export const roleQuickActions: Partial<Record<UserRole, RoleQuickAction[]>> = {
  pm: [
    { key: 'projects', label: 'Dự án', icon: 'ri-building-2-line', path: '/projects' },
    { key: 'boq', label: 'BOQ', icon: 'ri-file-list-3-line', path: '/construction/boq' },
    { key: 'progress', label: 'Tiến độ', icon: 'ri-bar-chart-line', path: '/progress' },
    { key: 'payments', label: 'Thanh toán', icon: 'ri-money-dollar-circle-line', path: '/payments' },
    { key: 'approvals', label: 'Phê duyệt', icon: 'ri-checkbox-circle-line', path: '/approvals' },
  ],
  qa_qc: [
    { key: 'quality', label: 'QA/QC', icon: 'ri-shield-check-line', path: '/quality' },
    { key: 'approvals', label: 'Phê duyệt', icon: 'ri-checkbox-circle-line', path: '/approvals' },
    { key: 'issues', label: 'Sự cố', icon: 'ri-alert-line', path: '/maintenance/issues' },
  ],
  qs: [
    { key: 'boq', label: 'BOQ', icon: 'ri-file-list-3-line', path: '/construction/boq' },
    { key: 'payments', label: 'Thanh toán', icon: 'ri-money-dollar-circle-line', path: '/payments' },
    { key: 'reports', label: 'Báo cáo', icon: 'ri-file-chart-line', path: '/reports' },
  ],
  site_manager: [
    { key: 'progress', label: 'Tiến độ', icon: 'ri-bar-chart-line', path: '/progress' },
    { key: 'inventory', label: 'Kho', icon: 'ri-store-3-line', path: '/inventory' },
    { key: 'wo', label: 'Work Orders', icon: 'ri-task-line', path: '/maintenance/work-orders' },
  ],
  warehouse: [
    { key: 'inventory', label: 'Kho VT', icon: 'ri-store-3-line', path: '/inventory' },
    { key: 'material', label: 'Đề xuất VT', icon: 'ri-archive-line', path: '/requests' },
  ],
  accountant: [
    { key: 'payments', label: 'Thanh toán', icon: 'ri-money-dollar-circle-line', path: '/payments' },
    { key: 'reports', label: 'Báo cáo', icon: 'ri-file-chart-line', path: '/reports' },
  ],
  secretary: [
    { key: 'approvals', label: 'Phê duyệt', icon: 'ri-checkbox-circle-line', path: '/approvals' },
    { key: 'quality', label: 'Hồ sơ QA', icon: 'ri-shield-check-line', path: '/quality' },
  ],
  staff: [
    { key: 'my-work', label: 'Việc của tôi', icon: 'ri-task-line', path: '/worker-dashboard' },
    { key: 'issues', label: 'Báo sự cố', icon: 'ri-alert-line', path: '/maintenance/issues' },
  ],
  site_foreman: [
    { key: 'foreman', label: 'Tổ của tôi', icon: 'ri-user-star-line', path: '/foreman' },
    { key: 'wo', label: 'Work Orders', icon: 'ri-task-line', path: '/maintenance/work-orders' },
  ],
  equipment_manager: [
    { key: 'equipment', label: 'Thiết bị', icon: 'ri-tools-line', path: '/equipment-dashboard' },
    { key: 'assets', label: 'Tài sản', icon: 'ri-seedling-line', path: '/maintenance/assets' },
  ],
  zone_manager: [
    { key: 'zones', label: 'Khu vực', icon: 'ri-map-2-line', path: '/zone-ops' },
    { key: 'plant', label: 'Cây xanh', icon: 'ri-plant-line', path: '/plant-intelligence' },
  ],
  agro_manager: [
    { key: 'agro', label: 'Nông hóa', icon: 'ri-flask-line', path: '/agro-dashboard' },
    { key: 'inventory', label: 'Kho', icon: 'ri-store-3-line', path: '/inventory' },
  ],
  supervisor: [
    { key: 'maintenance', label: 'Dashboard BĐ', icon: 'ri-plant-line', path: '/maintenance' },
    { key: 'wo', label: 'Work Orders', icon: 'ri-task-line', path: '/maintenance/work-orders' },
    { key: 'issues', label: 'Sự cố', icon: 'ri-alert-line', path: '/maintenance/issues' },
  ],
  director: [
    { key: 'reports', label: 'Báo cáo', icon: 'ri-file-chart-line', path: '/reports' },
    { key: 'projects', label: 'Dự án', icon: 'ri-building-2-line', path: '/projects' },
  ],
  admin: [
    { key: 'users', label: 'Người dùng', icon: 'ri-team-line', path: '/users' },
    { key: 'companies', label: 'Công ty', icon: 'ri-building-4-line', path: '/admin/companies' },
  ],
};

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Lấy danh sách menu item cho 1 role */
export function getNavItemsForRole(role: UserRole): NavItem[] {
  const items: NavItem[] = [];
  navigationConfig.forEach(group => {
    group.items.forEach(item => {
      if (item.roles.includes(role)) {
        items.push(item);
      }
    });
  });
  return items;
}

/** Lấy nhóm menu cho 1 role */
export function getNavGroupsForRole(role: UserRole): NavGroup[] {
  return navigationConfig
    .map(group => ({
      ...group,
      items: group.items.filter(item => item.roles.includes(role)),
    }))
    .filter(group => group.items.length > 0);
}
